import { useState } from 'react';
import { CustomerData, AddressData } from './types';
import { OrderFlow } from './OrderFlow';
import { PaymentSelection, PaymentMethod } from './PaymentSelection';
import { OrderConfirmation } from './OrderConfirmation';
import { StripeCheckout } from './StripeCheckout';
import { PhoneVerification } from './PhoneVerification';
import { PRICES, ENDPOINTS } from '../../config/constants';

interface OrderModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type OrderStep = 'order-flow' | 'payment' | 'verify-phone' | 'stripe-checkout' | 'confirmation';

export function OrderModal({ isOpen, onClose }: OrderModalProps) {
  const [step, setStep] = useState<OrderStep>('order-flow');
  const [isLoading, setIsLoading] = useState(false);
  const [orderData, setOrderData] = useState<{ customer: CustomerData; address: AddressData } | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('RAMBURS');
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderNumber, setOrderNumber] = useState<string>();
  const [error, setError] = useState<string>();
  const [stripeClientSecret, setStripeClientSecret] = useState<string>();
  const [, setPhoneVerified] = useState(false);

  const handleOrderFlowComplete = async (data: { customer: CustomerData; address: AddressData }) => {
    setOrderData(data);
    setStep('payment');
  };

  const createOrder = async () => {
    if (!orderData) return;

    try {
      setIsLoading(true);
      setError(undefined);
      
      // Create order request data
      const requestData = {
        customerData: {
          name: orderData.customer.name,
          email: orderData.customer.email,
          phone: orderData.customer.phone,
          address: {
            judet: orderData.address.judet,
            localitate: orderData.address.localitate,
            strada: orderData.address.strada,
            numar: orderData.address.numar,
            bloc: orderData.address.bloc || '',
            scara: orderData.address.scara || '',
            apartament: orderData.address.apartament || ''
          }
        },
        paymentMethod,
        quantity: 1,
        bookPrice: PRICES.BOOK,
        deliveryPrice: PRICES.DELIVERY,
        totalPrice: PRICES.TOTAL
      };

      // Create order
      const response = await fetch(ENDPOINTS.ORDERS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      const data = await response.json();

      if (!response.ok || !data.date?.orderNumber) {
        throw new Error(data.mesaj || 'Eroare la procesarea comenzii');
      }

      // Save order number before any step transition
      const orderNum = data.date.orderNumber;
      setOrderNumber(orderNum);

      // Process based on payment method
      if (paymentMethod === 'STRIPE') {
        if (!data.date.stripeClientSecret) {
          throw new Error('Nu s-a putut obține cheia de plată');
        }
        setStripeClientSecret(data.date.stripeClientSecret);
        setStep('stripe-checkout');
      } else if (paymentMethod === 'RAMBURS') {
        // For RAMBURS orders, initiate SMS verification
        try {
          // Send verification code
          const verifyResponse = await fetch(ENDPOINTS.SMS_VERIFICATION.SEND(orderNum), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
              phoneNumber: orderData.customer.phone 
            })
          });

          if (!verifyResponse.ok) {
            throw new Error('Nu s-a putut trimite codul de verificare');
          }

          // Only transition to verify-phone after successful code send
          setStep('verify-phone');
        } catch (verifyError) {
          console.error('Verification initiation error:', verifyError);
          throw new Error('Nu s-a putut iniția verificarea telefonului');
        }
      } else {
        setOrderSuccess(true);
        setStep('confirmation');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      setError(error instanceof Error ? error.message : 'Eroare la procesarea comenzii');
      setOrderSuccess(false);
      setStep('confirmation');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSubmit = async () => {
    try {
      await createOrder();
    } catch (error) {
      // Don't throw - errors are handled in createOrder
      console.error('Order creation error:', error);
    }
  };

  const handlePhoneVerified = () => {
    // Phone verification successful - AWB generation is handled by the backend
    setPhoneVerified(true);
    setOrderSuccess(true);
    setStep('confirmation');
  };

  const handleRetryPayment = () => {
    if (stripeClientSecret && orderNumber) {
      setError(undefined);
      setStep('stripe-checkout');
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setStep('order-flow');
      setOrderData(null);
      setPaymentMethod('RAMBURS');
      setOrderSuccess(false);
      setOrderNumber(undefined);
      setError(undefined);
      setStripeClientSecret(undefined);
      setPhoneVerified(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  const renderContent = () => {
    const baseModalClasses = `
      bg-gradient-to-b from-slate-900 via-slate-900 to-black 
      rounded-xl shadow-2xl border border-red-500/20 
      relative overflow-hidden w-full max-w-md mx-auto
      transform transition-all duration-300 ease-in-out
    `;

    const modalContent = () => {
      switch (step) {
        case 'order-flow':
          return (
            <div className="p-5">
              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-300 
                  focus:outline-none transition-colors duration-200"
              >
                <span className="sr-only">Închide</span>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Order summary */}
              <div className="mb-4 text-center">
                <h3 className="text-lg font-bold text-white mb-2">Sumar comandă</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Carte:</span>
                    <span className="text-white font-medium">{PRICES.BOOK.toFixed(2)} RON</span>
    </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Livrare:</span>
                    <span className="text-white font-medium">{PRICES.DELIVERY.toFixed(2)} RON</span>
                  </div>
                  <div className="flex justify-between items-center pt-1 border-t border-gray-700/50">
                    <span className="text-gray-200 font-medium">Total:</span>
                    <span className="text-white font-bold">{PRICES.TOTAL.toFixed(2)} RON</span>
                  </div>
                </div>
              </div>

              <OrderFlow onComplete={handleOrderFlowComplete} />
            </div>
          );

        case 'payment':
          return (
            <div className="p-5">
              <PaymentSelection
                selectedMethod={paymentMethod}
                onSelect={setPaymentMethod}
                isLoading={isLoading}
                onBack={() => setStep('order-flow')}
                onContinue={handlePaymentSubmit}
              />
            </div>
          );

        case 'verify-phone':
          if (!orderData || !orderNumber) return null;
          return (
            <div className="p-5">
              <PhoneVerification 
                orderId={Number(orderNumber)}
                phoneNumber={orderData.customer.phone}
                onVerified={handlePhoneVerified}
                onCancel={() => {
                  setIsLoading(false);
                  setStep('payment');
                  setError(undefined);
                }}
              />
            </div>
          );

        case 'stripe-checkout':
          return stripeClientSecret && orderNumber && (
            <div className="p-5">
              <StripeCheckout
                clientSecret={stripeClientSecret}
                orderNumber={orderNumber}
                onSuccess={() => {
                  setOrderSuccess(true);
                  setStep('confirmation');
                }}
                onError={(error) => {
                  setError(error);
                  setOrderSuccess(false);
                  setStep('confirmation');
                }}
                onBack={() => setStep('payment')}
              />
            </div>
          );

        case 'confirmation':
          return (
            <div className="p-5">
              <OrderConfirmation
                success={orderSuccess}
                orderNumber={orderNumber}
                paymentMethod={paymentMethod}
                error={error}
                onClose={handleClose}
                onRetryPayment={handleRetryPayment}
              />
            </div>
          );
      }
    };

    return (
      <div className={baseModalClasses}>
        {/* Gradient border effect */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-red-500 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-red-500 to-transparent" />
        <div className="absolute inset-y-0 left-0 w-px bg-gradient-to-b from-transparent via-red-500 to-transparent" />
        <div className="absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-red-500 to-transparent" />
        
        {/* Modal content */}
        {modalContent()}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="min-h-[calc(100vh-2rem)] flex items-center justify-center w-full">
        {renderContent()}
      </div>
    </div>
  );
}
